import { useState } from 'react';

import { PartUploadField } from '@/components/parts/PartUploadField';

import type { ScanUnits } from '@/services/scanToCad';

import { ScanToCadProgress } from './ScanToCadProgress';
import { ScanToCadResult } from './ScanToCadResult';
import {
  SCAN_MESH_ACCEPT,
  SCAN_MESH_EXTENSIONS,
  useScanUpload,
  type UploadedScan,
} from './useScanUpload';
import { useScanJob } from './useScanJob';

const UNIT_OPTIONS: ScanUnits[] = ['mm', 'cm', 'm', 'in'];

const UPLOAD_HINT = `Mesh scan only — ${SCAN_MESH_EXTENSIONS.map((e) => '.' + e).join(', ')}. Output is a parametric STEP file.`;

// The Scan-to-CAD flow: upload a mesh scan (real byte-progress bar) → pick units
// (the recon pipeline rejects unit-less input) → reconstruct (real stage
// progress) → download the STEP. Self-contained; reuses the parts-module
// PartUploadField for the upload UX and wires downloadSTEPFile for the output.
export function ScanToCadPanel() {
  const [uploaded, setUploaded] = useState<UploadedScan | null>(null);
  const [units, setUnits] = useState<ScanUnits>('mm');
  const { uploading, progress, error: uploadError, upload } = useScanUpload();
  const { job, jobId, running, error: jobError, start, reset } = useScanJob();

  const done = job?.status === 'success';

  const handleSelect = async (file: File) => {
    const result = await upload(file);
    if (result) setUploaded(result);
  };

  const handleReset = () => {
    reset();
    setUploaded(null);
  };

  if (done && job) {
    return <ScanToCadResult result={job} jobId={jobId} onReset={handleReset} />;
  }

  if (running && job) {
    return (
      <div className="space-y-3">
        <ScanToCadProgress
          stage={job.stage}
          progress={job.progress}
          message={job.message}
        />
        {jobError && <p className="text-[11px] text-red-400">{jobError}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PartUploadField
        enabled={!uploading}
        uploading={uploading}
        progress={progress}
        error={uploadError}
        meshName={uploaded?.meshName ?? ''}
        onSelect={handleSelect}
        onRemove={() => setUploaded(null)}
        label="Scan"
        ctaLabel="Upload mesh scan"
        accept={SCAN_MESH_ACCEPT}
        hint={UPLOAD_HINT}
      />

      <div className="space-y-1">
        <span className="text-xs text-telekinesis-neutral-50">Units</span>
        <div className="flex gap-1">
          {UNIT_OPTIONS.map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => setUnits(u)}
              className={`flex-1 rounded-md border px-2 py-1 text-[11px] ${
                units === u
                  ? 'border-[#107d40] text-[#107d40]'
                  : 'border-telekinesis-neutral-700 text-telekinesis-neutral-50 hover:border-[#107d40]'
              }`}
            >
              {u}
            </button>
          ))}
        </div>
        <p className="text-[10px] leading-snug text-telekinesis-neutral-400">
          A mesh carries no units — set the scan&apos;s real-world scale.
        </p>
      </div>

      <button
        type="button"
        disabled={!uploaded || uploading}
        onClick={() => uploaded && start(uploaded.meshRef, units)}
        className="w-full rounded-md bg-[#107d40] px-3 py-2 text-xs font-medium text-black hover:brightness-110 disabled:opacity-40"
      >
        Reconstruct to STEP
      </button>

      {jobError && <p className="text-[11px] text-red-400">{jobError}</p>}
    </div>
  );
}
